/**
 * Sektion Dokumente: Angebote & Rechnungen, Dokumentation, Abschluss.
 * Lieferscheine stehen nur noch unter „Material & Lieferanten" (Benjamin, 02.10.2026:
 * an zwei Stellen war es doppelt). Ein Beleg öffnet das Belegdetail wie auf der
 * Rechnungsseite — mit Bearbeiten, Versand und Folgebelegen, nicht nur dem PDF.
 * Aus pages/ProjektDetailPage.tsx herausgelöst (Plan Projekt-Tracking, Etappe 2) —
 * verschoben, nicht umgeschrieben.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useProjektAkte, useProjektRechnungsKandidaten, useRechnungZuordnen } from "../../hooks/usePlanung";
import BelegBlatt from "./BelegBlatt";
import Projektdokumentation from "./Projektdokumentation";
import ProjektMedien from "./ProjektMedien";
import Projektabschluss from "./Projektabschluss";
import { type Dokument } from "../DokumentLightbox";
import { registerZeilen, useInvoiceRegister } from "../../hooks/useInvoices";
import { belegArtLabel, invoiceDokument, KIND_LABEL, type InvoiceRegisterItem, type WebInvoiceDraftRecord } from "../../lib/invoices";
import type { RechnungKandidat } from "../../lib/types";
import { formatEur } from "../../lib/planer";
import { Sektion } from "./ProjektSektion";
import { useIstTelefon } from "../../hooks/useIstTelefon";
import { useMedien } from "../../hooks/useMedien";

/* ══ Dokumente: Angebote, Rechnungen, Dokumentation, Abschluss ══════════ */

/**
 * Rechnungen desselben Kunden ohne Projektbezug.
 *
 * Sie sind der häufigste Grund für einen zu niedrigen Auftragswert: nicht die
 * Leistung fehlt, sondern die Zuordnung. Im August 2026 lagen 35 Rechnungen über
 * 63.287 € herrenlos herum — darunter RE0155 über 15.861,67 €, die zum
 * Sirius-Projekt gehört und dort 17.331 € „offene Leistung" vortäuschte.
 */
export function HerrenloseRechnungen({ projektId }: { projektId: string }) {
  const { data: kandidaten = [] } = useProjektRechnungsKandidaten(projektId);
  const zuordnen = useRechnungZuordnen(projektId);
  if (kandidaten.length === 0) return null;
  return (
    <div className="vg-kandidaten">
      <p className="vg-detail-section-hint">
        <strong>{kandidaten.length} Rechnung{kandidaten.length === 1 ? "" : "en"} dieses Kunden ohne Projektbezug.</strong>{" "}
        Gehören sie hierher, fehlen sie im Auftragswert.
      </p>
      {kandidaten.map((r: RechnungKandidat) => (
        <div key={r.id} className="vg-kandidat-zeile">
          <span>
            <strong>{r.nummer}</strong> · {r.issue_date || "ohne Datum"} · {r.status}
          </span>
          <span className="vg-kandidat-betrag">{formatEur(r.netto, { round: true })}</span>
          <button
            type="button"
            className="vg-btn"
            disabled={zuordnen.isPending}
            onClick={() => zuordnen.mutate(r.id)}
          >
            Diesem Projekt zuordnen
          </button>
        </div>
      ))}
    </div>
  );
}

export function DokumenteSektion({ projektId, onZeigen }: { projektId: string; onZeigen: (d: Dokument) => void }) {
  // Nur die Belege dieses Projekts — der Server filtert, das Register lädt
  // nicht mehr komplett.
  const { data: register } = useInvoiceRegister({ projekt: projektId });
  const navigate = useNavigate();
  // Am Telefon wird nicht bearbeitet (20.10.2026): kein ✎, kein „Jetzt anlegen";
  // ein Entwurf öffnet dort die Ansicht.
  const telefon = useIstTelefon();
  const [blatt, setBlatt] = useState<InvoiceRegisterItem | null>(null);

  const zumProjekt = useMemo(
    () => registerZeilen(register).filter((r: InvoiceRegisterItem) => r.projekt_id === projektId),
    [register, projektId],
  );
  const angebote = zumProjekt.filter((r) => r.kind === "quote");
  const rechnungen = zumProjekt.filter((r) => r.kind !== "quote");
  /* Entwürfe gehören sichtbar hierher: sie sind der halbfertige Beleg dieses
     Projekts. Wer sie nur im Register fand, sah auf der Projektseite „noch kein
     Angebot erfasst" — und schrieb es ein zweites Mal. Sie tragen noch keine
     Nummer (die entsteht erst beim Festschreiben) und kein PDF, dafür führt ihr
     Knopf direkt in den Editor. */
  const entwuerfe = useMemo(
    () => (register?.pages[0]?.drafts ?? []).filter((d: WebInvoiceDraftRecord) => d.projekt_id === projektId),
    [register, projektId],
  );
  /* Fotos hängen an derselben Abfrage wie das Projektgedächtnis — hier wird
     nur die Zahl für den Reiter gebraucht, die Galerie baut
     `Projektdokumentation` aus demselben Cache. */
  const { data: akte } = useProjektAkte(projektId);
  const fotos = akte?.aufnahmen?.length ?? 0;
  /* Scans, SketchUp-Modelle, Renderings und Pläne haben einen eigenen Reiter (03.11.2026):
     unter der Fotogalerie im Reiter „Dokumentation" hat Volker das Modell nicht gefunden.
     Die SketchUp-Modelle stehen schon im zugeklappten Kopf, damit man weiß, dass es sie gibt. */
  const { data: medien = [] } = useMedien(projektId);
  const modelle = medien.filter((m) => m.art === "sketchup").length;

  /* Reiter statt gestapelter Blöcke: mit den Fotos wäre die Sektion
     sonst so lang geworden, dass die Belege unter der Galerie
     verschwinden. Die Zahl steht am Reiter, damit man einen leeren nicht erst
     aufmachen muss. */
  const [reiter, setReiter] = useState<"belege" | "dokumentation" | "dreid" | "abschluss">("belege");
  const REITER = [
    { id: "belege" as const, label: "Angebote & Rechnungen", anzahl: zumProjekt.length + entwuerfe.length },
    { id: "dokumentation" as const, label: "Fotos", anzahl: fotos },
    { id: "dreid" as const, label: "3D & Pläne", anzahl: medien.length },
    // Ohne Zahl: der Abschluss zaehlt nichts, er fasst zusammen.
    { id: "abschluss" as const, label: "Abschluss", anzahl: null },
  ];

  return (
    <Sektion
      titel="Dokumente"
      anker="dokumente"
      kopfRechts={
        <>
          {angebote.length} Angebote · {rechnungen.length} Rechnungen
          {entwuerfe.length > 0 ? ` · ${entwuerfe.length} Entwürfe` : ""}
          {fotos > 0 ? ` · ${fotos} Fotos` : ""}
          {modelle > 0 ? ` · ${modelle} SketchUp-${modelle === 1 ? "Modell" : "Modelle"}` : ""}
        </>
      }
    >
      <div className="vg-proj-tabs" role="tablist" aria-label="Dokumentarten">
        {REITER.map(({ id, label, anzahl }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={reiter === id}
            className={`vg-proj-tab${reiter === id ? " is-active" : ""}`}
            onClick={() => setReiter(id)}
          >
            {label}
            {anzahl === null ? null : <span className="vg-proj-tab-count">{anzahl}</span>}
          </button>
        ))}
      </div>

      {reiter === "abschluss" && (
        <Projektabschluss projektId={projektId} aktiv={reiter === "abschluss"} />
      )}

      {reiter === "dokumentation" && (
        <Projektdokumentation
          projektId={projektId}
          onBild={(bild) => onZeigen({ ...bild, bild: true })}
        />
      )}

      {reiter === "dreid" && <ProjektMedien projektId={projektId} />}

      <div className="vg-unterblock" hidden={reiter !== "belege"}>
        <div className="vg-detail-section-title">Angebote &amp; Rechnungen</div>
        {zumProjekt.length === 0 && entwuerfe.length === 0 ? (
          <div className="vg-detail-section-hint">
            Für dieses Projekt ist noch kein Angebot und keine Rechnung erfasst.{" "}
            {!telefon && <Link to={`/rechnungen/neu?projekt=${projektId}`}>Jetzt anlegen</Link>}
          </div>
        ) : (
          <table className="vg-tabelle">
            <thead>
              <tr>
                <th>Nummer</th><th>Art</th><th>Datum</th><th>Status</th>
                <th className="is-num">Netto</th><th className="is-num">Brutto</th>
                <th className="is-aktion" />
              </tr>
            </thead>
            <tbody>
              {entwuerfe.map((d: WebInvoiceDraftRecord) => (
                <tr key={d.id} className="vg-zeile-entwurf is-klickbar" tabIndex={0}
                  onClick={() => navigate(`/rechnungen/entwurf/${d.id}`)}
                  onKeyDown={(e) => { if (e.key === "Enter") navigate(`/rechnungen/entwurf/${d.id}`); }}>
                  <td><span className="is-muted">ohne Nummer</span></td>
                  <td className="is-muted">{belegArtLabel(d.kind, d.document?.source_quote_number)}</td>
                  <td className="is-muted">{(d.updated_at || "").slice(0, 10) || "—"}</td>
                  <td className="is-muted">Entwurf</td>
                  <td className="is-num">{formatEur(d.netto, { round: true })}</td>
                  <td className="is-num">{formatEur(d.brutto, { round: true })}</td>
                  <td className="is-aktion">
                    {!telefon && <Link
                      className="vg-icon-btn"
                      to={`/rechnungen/entwurf/${d.id}`}
                      onClick={(e) => e.stopPropagation()}
                      title={`${d.document?.doc_title || d.document?.subject || KIND_LABEL[d.kind]} bearbeiten`}
                      aria-label="Entwurf bearbeiten"
                    >
                      ✎
                    </Link>}
                  </td>
                </tr>
              ))}
              {zumProjekt.map((r: InvoiceRegisterItem) => (
                /* Ein Tipp öffnet das Belegdetail wie auf der Rechnungsseite (Dokument,
                   Versand, Status, Folgebeleg, „… ändern"); ✎ springt direkt in den Editor. */
                <tr key={r.id} className="is-klickbar" tabIndex={0} title="Beleg öffnen"
                  onClick={() => setBlatt(r)}
                  onKeyDown={(e) => { if (e.key === "Enter") setBlatt(r); }}>
                  <td>{r.nummer || <span className="is-muted">Entwurf</span>}</td>
                  <td className="is-muted">{r.kind === "quote" ? belegArtLabel(r.kind, r.source_quote_nummer) : "Rechnung"}</td>
                  <td className="is-muted">{r.issue_date || "—"}</td>
                  <td className="is-muted">{r.status}</td>
                  <td className="is-num">{formatEur(r.netto, { round: true })}</td>
                  <td className="is-num">{formatEur(r.brutto, { round: true })}</td>
                  <td className="is-aktion">
                    <span className="vg-doku-aktionen">
                      {/* Nur was Kontor selbst festgeschrieben hat, lässt sich im Editor ändern
                          (dieselbe Regel wie „… ändern" im Belegdetail). */}
                      {r.web_festgeschrieben_am && !telefon && (
                        <Link className="vg-icon-btn" to={`/rechnungen/beleg/${r.id}/bearbeiten`}
                          onClick={(e) => e.stopPropagation()} title={`${belegArtLabel(r.kind, r.source_quote_nummer)} ändern`}
                          aria-label={`${r.nummer} bearbeiten`}>✎</Link>
                      )}
                      {r.web_pdf_available ? (
                        <button
                          type="button"
                          className="vg-icon-btn"
                          title="PDF ansehen"
                          aria-label={`${r.nummer} als PDF ansehen`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onZeigen(invoiceDokument(r, `${r.issue_date || ""} · ${formatEur(r.brutto, { round: true })}`));
                          }}
                        >
                          ↗
                        </button>
                      ) : (
                        /* Belege aus der Desktop-Ära liegen als YAML/PDF auf Bens
                           Rechner; der Server kann sie nicht ausliefern. */
                        <span className="is-muted" title="PDF liegt nicht auf dem Server">—</span>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <HerrenloseRechnungen projektId={projektId} />
      </div>

      {blatt && <BelegBlatt beleg={blatt} onClose={() => setBlatt(null)} onUpdated={setBlatt} />}
    </Sektion>
  );
}
